import React, { useEffect } from "react";
import styled from "styled-components";

import { useDispatch, useSelector } from "services/redux/rootReducer";

import MenuBar from "../components/menuBar";
import GenericModal from "../components/modals/genericModal";
import Table from "../components/table";

import { EditorAction } from "../types/actions.editor";
import { TableAction } from "../types/actions.table";
import { ModalName, MultipleModalConfigs } from "../types/modal";
import { TableDefinition } from "../types/table";
import { ImportExportType } from "../types/importExport";
import { GENERIC_MODALS } from "../constants/modal";

interface Props<T> {
    data: Array<T>;
    indexer: string;
    modalConfig: MultipleModalConfigs;
    tableDefinition: TableDefinition<T>;
    form: React.FC<{
        onSubmitCallback: (fields: T) => void;
        submitBtnText: string;
        originalFields?: T;
        readOnlyMode?: boolean;
        initialForm: T;
    }>;
    initialForm: T;
    onAddCallback: (fields: T) => void;
    onUpdateCallback: (id: string, fields: T) => void;
    searchFunction: (searchKey: string, data: Array<T>) => Array<T>;
    onRemoveCallback: (ids: Array<string>) => void;
    onRefreshCallback?: () => void;
    onRawDataSubmitCallback?: (data: string | File) => void; // have to figure out whether this is CSV or JSON on parent
    onExportCallback?: (ids: Array<string>) => void;
    onModalPreloadCallback?: (id: string) => Promise<T>; // fires before modal is open, returning initial data override
}

const GenericEditorPage = <T extends Record<string, any>>({
    data: dataFromProps,
    indexer: indexerFromProps,
    modalConfig: modalConfigFromProps,
    tableDefinition,
    form,
    initialForm,
    onAddCallback,
    onRawDataSubmitCallback,
    onUpdateCallback,
    searchFunction,
    onRemoveCallback,
    onRefreshCallback,
    onModalPreloadCallback,
    onExportCallback,
}: Props<T>) => {
    // Contains ids of selected lists

    const { selectedItems } = useSelector((state) => state.table);
    const { modal, indexer, data, searchFilterKey, importExportConfig } =
        useSelector((state) => state.editor);
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch({
            type: EditorAction.SET_DATA,
            data: searchFilterKey
                ? searchFunction(searchFilterKey, dataFromProps)
                : dataFromProps,
            indexer: indexerFromProps,
        });
    }, [
        dispatch,
        searchFunction,
        dataFromProps,
        indexerFromProps,
        searchFilterKey,
    ]);

    useEffect(() => {
        if (modal?.name && modalConfigFromProps[modal?.name]) {
            dispatch({
                type: EditorAction.SET_MODAL_CONFIG,
                modalConfig: modalConfigFromProps[modal?.name],
            });
        }
    }, [dispatch, modal?.name, modalConfigFromProps]);

    const latestSelectedItem: T = (data || []).find(
        (item) =>
            item[indexer] === selectedItems[selectedItems.length - 1]?.value
    ) as T;

    const handleDeleteItems = () => {
        // deletes all selectedItems
        const itemsRemoved = selectedItems.map(
            (selectedItem) => selectedItem.value
        );
        if (
            window.confirm(
                `Are you sure you want to remove ${itemsRemoved.length} items?`
            )
        ) {
            dispatch({
                type: TableAction.SET_SELECTED_ITEMS,
                selectedItems: [],
            });
            onRemoveCallback(itemsRemoved);
        }
    };

    const handleExportItems = () => {
        // exports all selectedItems
        if (onExportCallback) {
            const toExport = selectedItems.map(
                (selectedItem) => selectedItem.value
            );
            onExportCallback(toExport);
        }
    };

    const handleSubmit = (submittedData: T | string | File) => {
        switch (modal?.name) {
            case ModalName.ADD: {
                switch (importExportConfig.importType) {
                    case ImportExportType.FORM:
                        onAddCallback(submittedData as T);
                        return;
                    case ImportExportType.JSON:
                    case ImportExportType.EXCEL:
                        if (onRawDataSubmitCallback) {
                            onRawDataSubmitCallback(
                                submittedData as string | File
                            );
                        }
                        return;
                    default:
                        return;
                }
            }
            case ModalName.UPDATE: {
                onUpdateCallback(
                    latestSelectedItem[indexer],
                    submittedData as T
                );
                return;
            }
            default:
                return;
        }
    };

    return (
        <Container>
            <div>
                <MenuBar
                    onDeleteCallback={handleDeleteItems}
                    onRefreshCallback={onRefreshCallback}
                    onModalPreloadCallback={onModalPreloadCallback}
                    onExportCallback={handleExportItems}
                />
                <hr />
            </div>
            <Table
                indexer={indexer}
                data={(data as T[]) || []}
                tableDefinition={tableDefinition}
                onModalPreloadCallback={onModalPreloadCallback}
            />
            {modal && GENERIC_MODALS.includes(modal.name) ? (
                <GenericModal
                    form={form}
                    initialForm={initialForm}
                    onSubmitCallback={handleSubmit}
                />
            ) : null}
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-flow: column;
    background: ${(props) => props.theme.colors.neutral[50]};

    & > *:first-child {
        flex: 0 0 6rem;

        display: flex;
        flex-flow: column;
        font-size: 1.4rem;
        font-weight: ${(props) => props.theme.fontWeight.bolder};
        color: ${(props) => props.theme.colors.neutral[800]};

        & > *:first-child {
            flex: 1;
        }

        & > *:last-child {
            flex: 0 0 1px;
            border: none;
            border-top: 1px solid ${(props) => props.theme.colors.neutral[125]};
            width: 95%;
            margin: 0;
            align-self: center;
        }
    }

    & > *:nth-child(2) {
        flex: 1;
    }
`;

export default GenericEditorPage;
